import Vue from 'vue';
import Router from 'vue-router';

/**
 * Components
 */
import LeftNav from '@/components/LeftNav.vue';
import RightNav from '@/components/RightNav.vue';
import TopNav from '@/components/TopNav.vue';
import UserAccounts from '@/components/UserAccounts.vue';
import TwofactorAuthenticate from '@/components/TwofactorAuthenticate.vue';

/**
 * Helper components assign globally
 */
import Spinner from '@/components/Helpers/Spinner.vue';
import Snackbar from '@/components/Helpers/Snackbar.vue';

/**
 * Pages
 */
import Login from '@/components/Pages/Login.vue';
import ForgotPassword from '@/components/Pages/ForgotPassword.vue';
import LoginWithPhoneNumber from '@/components/Pages/LoginWithPhoneNumber.vue';

import ResetPassword from '@/components/Pages/ResetPassword.vue';
import VerifyAccount from '@/components/Pages/VerifyAccount.vue';
import PageNotFound from '@/components/Pages/PageNotFound.vue';
/**
 * Logged in pages
 */
import Layout from '@/components/Layout.vue';
import Applications from '@/components/Pages/Applications.vue';
import SuperadminApps from '@/components/Pages/SuperadminApps.vue';
import Business from '@/components/Pages/Business.vue';
import Locations from '@/components/Pages/Locations.vue';
import Users from '@/components/Pages/Users.vue';
import Roles from '@/components/Pages/Roles.vue';
import Settings from '@/components/Pages/Settings.vue';

/**
 * Middlewares
 */
import Auth from '@/components/Middlewares/auth';
import NoAuth from '@/components/Middlewares/noAuth';
import ValidateToken from '@/components/Middlewares/validateToken';

/**
 * Image
 */
import ImageContainer from '@/plugins/attachment/ImageContainer.vue';
import AttachmentResources from '@/plugins/attachment/Resources.vue';
import AttachmentPopup from '@/plugins/attachment/AttachmentPopup.vue';

/**
 * New resource picker
 */

import ResourcePickerPopup from '@/components/Custom-components/ResourcePopup.vue';

/**
 * Custom components
 */
import OffsetHeading from './components/Custom-components/OffsetHeading.vue';
import OffsetBlock from './components/Custom-components/OffsetBlock.vue';
import PopupHeading from './components/Custom-components/PopupHeading.vue';
import BlockSpinner from './components/Custom-components/BlockSpinner.vue';
import ColorPicker from './components/Custom-components/ColorPicker.vue';

Vue.component('offset-block', OffsetBlock);
Vue.component('offset-heading', OffsetHeading);
Vue.component('popup-heading', PopupHeading);
Vue.component('block-spinner', BlockSpinner);
Vue.component('color-picker', ColorPicker);

Vue.component('image-container', ImageContainer);
Vue.component('attachment-resources', AttachmentResources);
Vue.component('attachment-popup', AttachmentPopup);

Vue.component('resource-picker-popup', ResourcePickerPopup);

/**
 * Register component
 */
Vue.component('Spinner', Spinner);
Vue.component('Snackbar', Snackbar);
Vue.component('LeftNav', LeftNav);
Vue.component('RightNav', RightNav);
Vue.component('TopNav', TopNav);
Vue.component('UserAccounts', UserAccounts);
Vue.component('two-factor-authenticate', TwofactorAuthenticate);

Vue.use(Router);
export default new Router({
  mode: 'history',
  routes: [
    /**
     * Normal routes
     */
    {
      path: '/',
      component: Login,
      name: 'Login',
      addtoNav: false,
      navText: 'Login',
      meta: {
        middleware: NoAuth,
      },
    },
    {
      path: '/phone-number-login',
      component: LoginWithPhoneNumber,
      name: 'LoginWithPhoneNumber',
      addtoNav: false,
      navText: 'Phone Number Login',
      meta: {
        middleware: NoAuth,
      },
    },
    {
      path: '/forgot-password',
      component: ForgotPassword,
      name: 'ForgotPassword',
      addtoNav: false,
      navText: 'Forgot Password',
      meta: {
        middleware: NoAuth,
      },
    },
    {
      path: '/new-password',
      component: ResetPassword,
      name: 'ResetPassword',
      addtoNav: false,
      navText: 'Reset Password',
      meta: {
        middleware: NoAuth,
      },
    },
    {
      path: '/activate',
      component: VerifyAccount,
      name: 'VerifyAccount',
      addtoNav: false,
      navText: 'Verify Account',
      meta: {
        middleware: NoAuth,
      },
    },

    /**
     * Logged in routes
     */
    {
      path: '/home',
      component: Layout,
      name: 'Layout',
      addtoNav: false,
      navText: 'Home',
      checkAccess: false,
      meta: {
        pageTitle: 'Home',
        middleware: Auth,
      },
      children: [
        {
          path: '/applications',
          component: Applications,
          name: 'Applications',
          addtoNav: true,
          navText: 'Applications',
          checkAccess: '',
          icon: 'apps',
          meta: {
            pageTitle: 'Applications',
            middleware: [Auth, ValidateToken],
          },
        },
        {
          path: '/superadmin-applications',
          component: SuperadminApps,
          name: 'SuperadminApps',
          addtoNav: true,
          navText: 'Superadmin Apps',
          checkAccess: 'is_superadmin',
          icon: 'apps',
          meta: {
            pageTitle: 'Superadmin Apps',
            middleware: [Auth, ValidateToken],
          },
        },
        {
          path: '/business',
          component: Business,
          name: 'Business',
          addtoNav: true,
          navText: 'Business',
          checkAccess: 'is_unoapp|is_superadmin',
          icon: 'business',
          meta: {
            pageTitle: 'Business',
            middleware: [Auth, ValidateToken],
          },
        },
        {
          path: '/locations',
          component: Locations,
          name: 'Locations',
          addtoNav: true,
          navText: 'Locations',
          checkAccess: '',
          icon: 'location_on',
          meta: {
            pageTitle: 'Locations',
            middleware: [Auth, ValidateToken],
          },
        },
        {
          path: '/users',
          component: Users,
          name: 'Users',
          addtoNav: true,
          navText: 'Users',
          checkAccess: '',
          icon: 'fas fa-user-circle',
          meta: {
            pageTitle: 'Users',
            middleware: [Auth, ValidateToken],
          },
        },
        {
          path: '/roles',
          component: Roles,
          name: 'Roles',
          addtoNav: true,
          navText: 'Roles',
          checkAccess: 'is_superadmin|is_unoapp|is_corporate',
          icon: 'people',
          meta: {
            pageTitle: 'Roles',
            middleware: [Auth, ValidateToken],
          },
        },
        {
          path: '/settings',
          component: Settings,
          name: 'Settings',
          addtoNav: true,
          navText: 'Settings',
          checkAccess: '',
          icon: 'fas fa-cog',
          styleClass: 'settings',
          meta: {
            pageTitle: 'Settings',
            middleware: [Auth, ValidateToken],
          },
        },
      ],
    },
    {
      path: '*',
      addtoNav: false,
      name: '404',
      component: PageNotFound,
    },
  ],
});
